import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import { useToast } from "@/hooks/use-toast";

interface DemoWallet {
  id: string;
  balance: number;
  initial_balance: number;
  is_active: boolean;
  activated_at: string;
  expires_at: string;
}

interface DemoProgress {
  browsed: boolean;
  purchased: boolean;
  viewedPassport: boolean;
  listedMarketplace: boolean;
}

interface DemoContextType {
  isDemoActive: boolean;
  demoWallet: DemoWallet | null;
  demoProgress: DemoProgress;
  timeRemaining: string;
  isExpired: boolean;
  activateDemo: () => Promise<void>;
  deactivateDemo: () => Promise<void>;
  refreshDemoWallet: () => Promise<void>;
  updateProgress: (step: keyof DemoProgress) => void;
  completedSteps: number;
  totalSteps: number;
  loading: boolean;
  isDemoEnabled: boolean;
}

const DemoContext = createContext<DemoContextType>({ isDemoEnabled: false } as DemoContextType);

export const useDemo = () => useContext(DemoContext);

const PROGRESS_KEY = "chawni-demo-progress";

export const DemoProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [demoWallet, setDemoWallet] = useState<DemoWallet | null>(null);
  const [isDemoEnabled, setIsDemoEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [isExpired, setIsExpired] = useState(false);
  const [demoProgress, setDemoProgress] = useState<DemoProgress>({
    browsed: false,
    purchased: false,
    viewedPassport: false,
    listedMarketplace: false,
  });

  const isDemoActive = !!demoWallet?.is_active && !isExpired;

  const refreshDemoWallet = useCallback(async () => {
    if (!user) { setDemoWallet(null); setLoading(false); return; }
    try {
      const { data } = await supabase
        .from("demo_wallets" as any)
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      const row = data as any;
      if (row) {
        setDemoWallet(row);
        const expired = row.expires_at ? new Date(row.expires_at) < new Date() : false;
        setIsExpired(expired);
        if (expired && row.is_active) {
          await supabase.from("demo_wallets" as any).update({ is_active: false }).eq("id", row.id);
        }
      } else {
        setDemoWallet(null);
      }
    } catch (err: any) {
      console.warn("DemoContext: Failed to refresh wallet:", err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshDemoWallet();
    const fetchGlobalSetting = async () => {
      try {
        const { data, error } = await supabase.from("store_settings").select("value").eq("key", "demo_enabled").maybeSingle();
        if (error) throw error;
        // Only disable if explicitly set to false or "false"
        if (data && (data.value === false || data.value === "false")) {
          setIsDemoEnabled(false);
        } else {
          setIsDemoEnabled(true);
        }
      } catch (err) {
        console.warn("DemoContext: Using default (enabled) for demo mode as store_settings failed.", err);
        setIsDemoEnabled(true);
      }
    };
    fetchGlobalSetting();
  }, [refreshDemoWallet, user]);

  // Load progress from localStorage
  useEffect(() => {
    if (!user) return;
    try {
      const stored = JSON.parse(localStorage.getItem(`${PROGRESS_KEY}-${user.id}`) || "{}");
      setDemoProgress((prev) => ({ ...prev, ...stored }));
    } catch {}
  }, [user]);

  // Countdown timer
  useEffect(() => {
    if (!demoWallet || isExpired) { setTimeRemaining(""); return; }
    const update = () => {
      const diff = new Date(demoWallet.expires_at).getTime() - Date.now();
      if (diff <= 0) { setIsExpired(true); setTimeRemaining("Expired"); return; }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      setTimeRemaining(`${days}d ${hours}h ${mins}m`);
    };
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [demoWallet, isExpired]);

  const activateDemo = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Set expiration to 3 days from now by default if no setting found
      const { data: durationData } = await supabase.from("store_settings").select("value").eq("key", "demo_duration_days").maybeSingle();
      const days = durationData?.value ? Number(durationData.value) : 3;
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + days);

      const { data: amountData } = await supabase.from("store_settings").select("value").eq("key", "demo_wallet_amount").maybeSingle();
      const amount = amountData?.value ? Number(amountData.value) : 10000;

      const { data, error } = await supabase
        .from("demo_wallets" as any)
        .upsert({ 
          user_id: user.id, 
          balance: amount, 
          initial_balance: amount,
          is_active: true,
          activated_at: new Date().toISOString(),
          expires_at: expiresAt.toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      setDemoWallet(data as any);
      setIsExpired(false);
      toast({
        title: "Demo Activated",
        description: `You've received ${amount.toLocaleString()} DH in demo money!`,
      });
    } catch (err: any) {
      console.error("Demo activation error:", err.message);
      toast({
        title: "Activation Failed",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const deactivateDemo = async () => {
    if (!user || !demoWallet) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from("demo_wallets" as any)
        .update({ is_active: false })
        .eq("user_id", user.id);
        
      if (error) throw error;
      await refreshDemoWallet();
      toast({
        title: "Demo Mode Ended",
        description: "You've successfully returned to the standard experience.",
      });
    } catch (err: any) {
      console.error("Demo deactivation error:", err.message);
      toast({
        title: "Deactivation Failed",
        description: err.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateProgress = useCallback((step: keyof DemoProgress) => {
    if (!user) return;
    setDemoProgress((prev) => {
      const updated = { ...prev, [step]: true };
      localStorage.setItem(`${PROGRESS_KEY}-${user.id}`, JSON.stringify(updated));
      return updated;
    });
  }, [user]);

  const completedSteps = Object.values(demoProgress).filter(Boolean).length;
  const totalSteps = 4;

  return (
    <DemoContext.Provider value={{
      isDemoActive, demoWallet, demoProgress, timeRemaining, isExpired,
      activateDemo, deactivateDemo, refreshDemoWallet, updateProgress,
      completedSteps, totalSteps, loading, isDemoEnabled,
    }}>
      {children}
    </DemoContext.Provider>
  );
};
